import { useState, useEffect, useCallback } from "react";
import api from "../api";

const CACHE_KEY = "exam_mode_cache";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export default function useExamMode(studentId = null) {
  const [mockExamsMode, setMockExamsMode] = useState("actual");
  const [worksheetsMode, setWorksheetsMode] = useState("actual");
  const [toggleEnabled, setToggleEnabled] = useState(false);
  const [preferenceMode, setPreferenceMode] = useState("actual");
  const [loading, setLoading] = useState(true);
  const [settingMode, setSettingMode] = useState(false);

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isParent = role === "parent";
  const basePath = isParent ? "/parent" : "/student";

  const fetchMode = useCallback(async () => {
    let hasFreshCache = false;
    try {
      // Cache key scoped to student when parent
      const cacheKey = isParent && studentId ? `${CACHE_KEY}_${studentId}` : CACHE_KEY;

      // Check sessionStorage cache first
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL) {
          setMockExamsMode(parsed.mock_exams || "actual");
          setWorksheetsMode(parsed.worksheets || "actual");
          setToggleEnabled(!!parsed.toggle_enabled);
          setPreferenceMode(parsed.preference_mode || "actual");
          setLoading(false);
          hasFreshCache = true;
        }
      }

      const params = {};
      if (isParent && studentId) params.student_id = studentId;

      const res = await api.get(`${basePath}/exam-mode`, { params });
      const data = res.data?.data || {};
      const mockMode = data.mock_exams || "actual";
      const wsMode = data.worksheets || "actual";
      const toggle = !!data.toggle_enabled;
      const preference = data.preference_mode || "actual";

      setMockExamsMode(mockMode);
      setWorksheetsMode(wsMode);
      setToggleEnabled(toggle);
      setPreferenceMode(preference);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          mock_exams: mockMode,
          worksheets: wsMode,
          toggle_enabled: toggle,
          preference_mode: preference,
          timestamp: Date.now(),
        })
      );
    } catch {
      // Keep fresh cached values on request failure; fallback to actual only when nothing cached.
      if (!hasFreshCache) {
        setMockExamsMode("actual");
        setWorksheetsMode("actual");
        setToggleEnabled(false);
        setPreferenceMode("actual");
      }
    } finally {
      // If cache already rendered, this is a background refresh.
      if (!hasFreshCache) setLoading(false);
    }
  }, [basePath, isParent, studentId]);

  useEffect(() => {
    fetchMode();
  }, [fetchMode]);

  const isTestingMode = useCallback(
    (scope) => {
      if (scope === "mock_exams") return mockExamsMode === "testing";
      // Past papers use the same portal practice toggle as mock exams (no purchase bypass).
      if (scope === "past_papers") return mockExamsMode === "testing";
      if (scope === "worksheets") return worksheetsMode === "testing";
      return mockExamsMode === "testing" || worksheetsMode === "testing";
    },
    [mockExamsMode, worksheetsMode]
  );

  const refresh = useCallback(() => {
    const cacheKey = isParent && studentId ? `${CACHE_KEY}_${studentId}` : CACHE_KEY;
    sessionStorage.removeItem(cacheKey);
    setLoading(true);
    fetchMode();
  }, [fetchMode, isParent, studentId]);

  const setMode = useCallback(
    async (mode, targetStudentId = null) => {
      setSettingMode(true);
      try {
        const payload = { mode };
        if (isParent && (targetStudentId || studentId)) {
          payload.student_id = targetStudentId || studentId;
        }
        await api.post(`${basePath}/exam-mode`, payload);
        window.dispatchEvent(
          new CustomEvent("exam-mode-changed", {
            detail: {
              mode,
              student_id: payload.student_id || null,
              at: Date.now(),
            },
          })
        );
        refresh();
      } finally {
        setSettingMode(false);
      }
    },
    [basePath, isParent, refresh, studentId]
  );

  return {
    mockExamsMode,
    worksheetsMode,
    toggleEnabled,
    preferenceMode,
    isTestingMode,
    loading,
    settingMode,
    refresh,
    setMode,
  };
}
